import { Chart, registerables } from 'chart.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { ChartLayout, OverviewContent, RelevanceContent, EquityContent, MethodologyContent } from './ChartLayout';
import { KeyMetrics, KeyMetric } from './KeyMetrics';
import './ChartLayout.css';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import PopulationGrowth from './PopulationGrowth';
import PopulationGrowthComparison from './PopulationGrowthComparison';

Chart.register(...registerables);

interface Props {
  location: string;
  geofips: number | string | null;
  preferredState?: string | null;
  yearStart?: number;
  yearEnd?: number;
}

type Row = {
  geofips: number;
  location_name: string;
  county: string | null;
  state: string;
  geography_type: 'state' | 'county' | 'place' | string;
  year: number;
  housing_units: number | null;
  num_employees: number | null;
};

export const HousingToEmployees: React.FC<Props> = ({
  location,
  geofips,
  preferredState = null,
  yearStart = 2012,
  yearEnd = 2023
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataRows, setDataRows] = useState<Row[]>([]);
  const [activeView, setActiveView] = useState<string>('overview');
  const [activeChart, setActiveChart] = useState<'ratio' | 'projection' | 'comparison'>('ratio');
  const [comparisonCityPercentChange, setComparisonCityPercentChange] = useState<number | null>(null);
  const [comparisonCaliforniaPercentChange, setComparisonCaliforniaPercentChange] = useState<number | null>(null);
  const [projectionPopulationIn10Years, setProjectionPopulationIn10Years] = useState<number | null>(null);
  const [projectionTargetYear, setProjectionTargetYear] = useState<number | null>(null);

  const years = useMemo(
    () => Array.from({ length: (yearEnd - yearStart + 1) }, (_, i) => yearStart + i),
    [yearStart, yearEnd]
  );

  useEffect(() => {
    const fetchData = async () => {
      // Add early return if location is not set
      if (!location || location === '') {
        console.log('No location set yet, waiting...');
        setLoading(false);
        return;
      }
      
      if (!geofips) {
        setError('A geofips is required to render this chart.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('Fetching housing_to_employees data for location:', location, 'geofips:', geofips);
        console.log('Year range:', yearStart, 'to', yearEnd);
        
        let allData = null;
        let allErr = null;
        
        // Check if this is California state (geofips 6) - prioritize state query
        const isCaliforniaState = geofips === 6 || geofips === '6' || (location && location.toLowerCase() === 'california');
        
        if (isCaliforniaState) {
          console.log('Detected California state - querying state data directly');
          const { data: stateData, error: stateErr } = await supabaseClient
            .from('housing_to_employees')
            .select('*')
            .eq('geography_type', 'state')
            .eq('geofips', 6)
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true });
          
          if (stateErr) {
            console.error('Error fetching California state data:', stateErr);
            allErr = stateErr;
          }
          
          console.log('California state query result:', stateData?.length || 0, 'rows');
          if (stateData && stateData.length > 0) {
            console.log('Found California state data:', stateData.length, 'rows');
            console.log('Sample state data:', stateData[0]);
            allData = stateData;
          } else {
            console.log('No California state data found');
            allData = [];
          }
        } else {
          // Try place (city) data first, then county, then state
          // Prefer geofips (unambiguous); location_name ilike matches e.g. "South San Francisco" when selecting "San Francisco"
          let query = supabaseClient
            .from('housing_to_employees')
            .select('*')
            .eq('geography_type', 'place')
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true });
          
          if (geofips) {
            const geofipsNum = typeof geofips === 'string' ? parseInt(String(geofips).replace(/^0+/, '') || '0', 10) : Number(geofips);
            if (!Number.isNaN(geofipsNum)) {
              query = query.eq('geofips', geofipsNum);
            }
          }
          if (!geofips && location) {
            query = query.eq('location_name', location); // exact match to avoid "South San Francisco" when selecting "San Francisco"
          }
          
          const { data: placeData, error: placeErr } = await query;
          
          if (placeErr) {
            console.error('Error fetching place data:', placeErr);
          }
          
          console.log('Place query result:', placeData?.length || 0, 'rows');
          if (placeData && placeData.length > 0) {
            console.log('Found place (city) data:', placeData.length, 'rows');
            console.log('Sample place data:', placeData[0]);
            allData = placeData;
          } else {
            console.log('No place data found, trying county data...');
            
            // Try county data
            let countyQuery = supabaseClient
              .from('housing_to_employees')
              .select('*')
              .eq('geography_type', 'county')
              .gte('year', yearStart)
              .lte('year', yearEnd)
              .order('year', { ascending: true });
            
            if (geofips) {
              const g = typeof geofips === 'string' ? parseInt(String(geofips).replace(/^0+/, '') || '0', 10) : Number(geofips);
              if (!Number.isNaN(g)) countyQuery = countyQuery.eq('geofips', g);
            }
            if (!geofips && location) {
              countyQuery = countyQuery.eq('location_name', location);
            }
            
            const { data: countyData, error: countyErr } = await countyQuery;
            
            if (countyErr) {
              console.error('Error fetching county data:', countyErr);
            }
            
            console.log('County query result:', countyData?.length || 0, 'rows');
            if (countyData && countyData.length > 0) {
              console.log('Found county data:', countyData.length, 'rows');
              console.log('Sample county data:', countyData[0]);
              allData = countyData;
            } else {
              console.log('No county data found, trying state data...');
              
              // Try state data
              let stateQuery = supabaseClient
                .from('housing_to_employees')
                .select('*')
                .eq('geography_type', 'state')
                .gte('year', yearStart)
                .lte('year', yearEnd)
                .order('year', { ascending: true });
              
              if (geofips) {
                const g = typeof geofips === 'string' ? parseInt(String(geofips).replace(/^0+/, '') || '0', 10) : Number(geofips);
                if (!Number.isNaN(g)) stateQuery = stateQuery.eq('geofips', g);
              }
              if (!geofips && location) {
                stateQuery = stateQuery.eq('location_name', location);
              }
              
              const { data: stateData, error: stateErr } = await stateQuery;
              
              if (stateErr) {
                console.error('Error fetching state data:', stateErr);
              }
              
              console.log('State query result:', stateData?.length || 0, 'rows');
              if (stateData && stateData.length > 0) {
                console.log('Found state-level data:', stateData.length, 'rows');
                console.log('Sample state data:', stateData[0]);
                allData = stateData;
              } else {
                console.log('No data found for any geography type');
                allData = [];
              }
              allErr = stateErr; // Assign the last error encountered
            }
          }
        }

        if (allErr) {
          console.error('Error fetching data:', allErr);
          throw allErr;
        }

        let rows = allData || [];
        // Dedupe: if multiple rows per year (e.g. place + county), pick one—prefer place, then highest housing_units
        if (rows.length > 0 && location) {
          const byYear = new Map<number, Row[]>();
          for (const r of rows) {
            const list = byYear.get(r.year) || [];
            list.push(r as Row);
            byYear.set(r.year, list);
          }
          rows = Array.from(byYear.values()).map((yearRows) => {
            if (yearRows.length === 1) return yearRows[0];
            const exact = yearRows.find((r) => r.location_name === location);
            if (exact) return exact;
            const placeFirst = yearRows.sort((a, b) => {
              if (a.geography_type === 'place' && b.geography_type !== 'place') return -1;
              if (a.geography_type !== 'place' && b.geography_type === 'place') return 1;
              return (b.housing_units ?? 0) - (a.housing_units ?? 0);
            });
            return placeFirst[0];
          });
          rows.sort((a, b) => a.year - b.year);
        }
        setDataRows(rows);
        setLoading(false);
      } catch (e: any) {
        console.error('Error loading housing_to_employees:', e);
        setError(e.message || 'Failed to load housing to employees data');
        setLoading(false);
      }
    };

    fetchData();
  }, [geofips, yearStart, yearEnd, location]);

  // Fetch population comparison percent changes when Population Growth tab is active
  useEffect(() => {
    if (activeChart !== 'comparison' || !location) {
      setComparisonCityPercentChange(null);
      setComparisonCaliforniaPercentChange(null);
      return;
    }
    let cancelled = false;
    const fetchComparisonMetrics = async () => {
      try {
        const [locationResult, californiaResult] = await Promise.all([
          supabaseClient
            .from('population')
            .select('year, population')
            .eq('location_name', location)
            .gte('year', 1990)
            .lte('year', 2045)
            .order('year', { ascending: true }),
          supabaseClient
            .from('population')
            .select('year, population')
            .eq('location_name', 'California')
            .gte('year', 1990)
            .lte('year', 2045)
            .order('year', { ascending: true })
        ]);
        if (cancelled || locationResult.error || californiaResult.error) return;
        const locationData = locationResult.data || [];
        const californiaData = californiaResult.data || [];
        if (locationData.length === 0 || californiaData.length === 0) return;
        const locationByYear = new Map<number, number>();
        locationData.forEach((r: { year?: number; population?: number }) => {
          if (r.year != null && r.population != null) locationByYear.set(r.year, r.population);
        });
        const californiaByYear = new Map<number, number>();
        californiaData.forEach((r: { year?: number; population?: number }) => {
          if (r.year != null && r.population != null) californiaByYear.set(r.year, r.population);
        });
        const sharedYears = Array.from(locationByYear.keys())
          .filter(y => californiaByYear.has(y))
          .sort((a, b) => a - b);
        if (sharedYears.length === 0) return;
        const baselineYear = sharedYears[0];
        const locationBaseline = locationByYear.get(baselineYear) || 0;
        const californiaBaseline = californiaByYear.get(baselineYear) || 0;
        if (locationBaseline <= 0 || californiaBaseline <= 0) return;
        const latestYear = sharedYears[sharedYears.length - 1];
        const locationLatest = locationByYear.get(latestYear) || 0;
        const californiaLatest = californiaByYear.get(latestYear) || 0;
        const cityPct = locationLatest > 0 ? ((locationLatest / locationBaseline) - 1) * 100 : null;
        const calPct = californiaLatest > 0 ? ((californiaLatest / californiaBaseline) - 1) * 100 : null;
        if (!cancelled) {
          setComparisonCityPercentChange(cityPct);
          setComparisonCaliforniaPercentChange(calPct);
        }
      } catch {
        if (!cancelled) {
          setComparisonCityPercentChange(null);
          setComparisonCaliforniaPercentChange(null);
        }
      }
    };
    fetchComparisonMetrics();
    return () => { cancelled = true; };
  }, [activeChart, location]);

  // Fetch population projection (10 years from last data point) when Population Projection tab is active
  useEffect(() => {
    if (activeChart !== 'projection' || !location) {
      setProjectionPopulationIn10Years(null);
      setProjectionTargetYear(null);
      return;
    }
    let cancelled = false;
    const fetchProjectionMetric = async () => {
      try {
        const { data, error: err } = await supabaseClient
          .from('population')
          .select('year, population')
          .eq('location_name', location)
          .gte('year', 1990)
          .lte('year', 2045)
          .order('year', { ascending: true });
        if (cancelled || err || !data || data.length === 0) return;
        const maxYear = Math.max(...data.map((r: { year: number }) => r.year));
        // Use last historical year (≤2023) as baseline so "+10 years" lands in typical projection range (e.g. 2033)
        const historicalYears = data
          .filter((r: { year: number }) => r.year <= 2023)
          .map((r: { year: number }) => r.year);
        const lastDataYear = historicalYears.length > 0 ? Math.max(...historicalYears) : maxYear;
        const targetYear = lastDataYear + 10;
        const targetRow = data.find((r: { year: number }) => r.year === targetYear);
        const population = targetRow != null && targetRow.population != null
          ? Number(targetRow.population)
          : null;
        if (!cancelled) {
          setProjectionTargetYear(targetYear);
          setProjectionPopulationIn10Years(population);
        }
      } catch {
        if (!cancelled) {
          setProjectionPopulationIn10Years(null);
          setProjectionTargetYear(null);
        }
      }
    };
    fetchProjectionMetric();
    return () => { cancelled = true; };
  }, [activeChart, location]);

  // Calculate key metrics
  const keyMetrics = useMemo(() => {
    if (loading || dataRows.length === 0) return null;
    
    const years = dataRows.map(r => r.year).filter(y => !isNaN(y));
    if (years.length === 0) return null;
    
    const latestYear = Math.max(...years);
    const latestRow = dataRows.find(r => r.year === latestYear);
    
    if (!latestRow || 
        latestRow.housing_units === null || 
        latestRow.housing_units === undefined ||
        latestRow.num_employees === null || 
        latestRow.num_employees === undefined) {
      return null;
    }
    
    const housingUnits = Number(latestRow.housing_units);
    const employees = Number(latestRow.num_employees);
    
    if (isNaN(housingUnits) || isNaN(employees)) {
      return null;
    }
    
    const ratio = employees > 0 
      ? (housingUnits / employees).toFixed(2)
      : 'N/A';
    
    return {
      housingUnits: housingUnits,
      employees: employees,
      ratio: ratio,
      latestYear: latestYear
    };
  }, [loading, dataRows]);

  // Earliest/latest years and ratio trend for narrative (ratio view only)
  const ratioNarrativeMeta = useMemo(() => {
    if (dataRows.length === 0) return null;
    const yearVals = dataRows.map(r => r.year).filter(y => !isNaN(y));
    if (yearVals.length === 0) return null;
    const earliestYear = Math.min(...yearVals);
    const latestYear = Math.max(...yearVals);
    const ratiosByYear: { year: number; ratio: number }[] = [];
    for (const row of dataRows) {
      const hu = row.housing_units != null ? Number(row.housing_units) : NaN;
      const emp = row.num_employees != null ? Number(row.num_employees) : NaN;
      if (!isNaN(hu) && !isNaN(emp) && emp > 0) {
        ratiosByYear.push({ year: row.year, ratio: hu / emp });
      }
    }
    ratiosByYear.sort((a, b) => a.year - b.year);
    if (ratiosByYear.length < 2) {
      return { earliestYear, latestYear, trend: 'mixed' as const };
    }
    const first = ratiosByYear[0].ratio;
    const last = ratiosByYear[ratiosByYear.length - 1].ratio;
    const pctChange = first !== 0 ? (last - first) / first : 0;
    const hasIncrease = ratiosByYear.some((r, i) => i > 0 && r.ratio > ratiosByYear[i - 1].ratio);
    const hasDecrease = ratiosByYear.some((r, i) => i > 0 && r.ratio < ratiosByYear[i - 1].ratio);
    if (Math.abs(pctChange) < 0.05) {
      return { earliestYear, latestYear, trend: 'stable' as const };
    }
    if (hasIncrease && hasDecrease) {
      return { earliestYear, latestYear, trend: 'mixed' as const };
    }
    if (pctChange > 0) return { earliestYear, latestYear, trend: 'upward' as const };
    if (pctChange < 0) return { earliestYear, latestYear, trend: 'downward' as const };
    return { earliestYear, latestYear, trend: 'mixed' as const };
  }, [dataRows]);

  // Build the chart after data loads
  useEffect(() => {
    if (activeChart !== 'ratio') {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
      return;
    }

    if (loading || dataRows.length === 0) return;

    const ctx = chartRef.current?.getContext('2d');
    if (!ctx) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    console.log('Building chart with dataRows:', dataRows);
    console.log('Sample row:', dataRows[0]);

    // Prepare data for housing units and employees
    const housingData = years.map(year => {
      const row = dataRows.find(r => r.year === year);
      const value = row && row.housing_units !== null ? row.housing_units : null;
      if (value !== null) {
        console.log(`Year ${year}: housing_units = ${value}`);
      }
      return value;
    });

    const employeesData = years.map(year => {
      const row = dataRows.find(r => r.year === year);
      const value = row && row.num_employees !== null ? row.num_employees : null;
      if (value !== null) {
        console.log(`Year ${year}: num_employees = ${value}`);
      }
      return value;
    });

    console.log('Housing data array:', housingData);
    console.log('Employees data array:', employeesData);

    const chartTitle = `Housing Units and Employees - ${location || 'Selected Location'}`;

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: years,
        datasets: [
          {
            label: 'Housing Units',
            data: housingData,
            borderColor: '#3b82f6', // Blue
            backgroundColor: '#3b82f633',
            borderWidth: 2,
            spanGaps: true,
            tension: 0.25,
            pointRadius: 3
          },
          {
            label: 'Employees',
            data: employeesData,
            borderColor: '#ef4444', // Red
            backgroundColor: '#ef444433',
            borderWidth: 2,
            spanGaps: true,
            tension: 0.25,
            pointRadius: 3
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'nearest', intersect: false },
        scales: {
          y: {
            title: { display: true, text: 'Count' },
            beginAtZero: true,
            ticks: {
              callback: (v: any) => {
                if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
                if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
                return v.toString();
              }
            }
          },
          x: {
            title: { display: true, text: 'Year' }
          }
        },
        plugins: {
          title: standardTitle(chartTitle),
          legend: standardLegend,
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed.y;
                return `${ctx.dataset.label}: ${val != null ? val.toLocaleString() : 'N/A'}`;
              }
            }
          }
        }
      }
    });
  }, [activeChart, loading, years, dataRows, location]);

  const handleDownloadPNG = () => {
    const filename = formatFilename(location, 'Housing_to_Employees');
    downloadChartAsPNG(chartRef, filename);
  };

  const handleDownloadCSV = () => {
    const rows: Array<Record<string, any>> = dataRows.map(row => ({
      year: row.year,
      location: row.location_name,
      county: row.county,
      state: row.state,
      geography_type: row.geography_type,
      housing_units: row.housing_units,
      num_employees: row.num_employees
    }));
    downloadDataAsCSV(rows, formatFilename(location, 'Housing_to_Employees_Data'));
  };

  if (loading) {
    return (
      <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading housing to employees data for {location || 'selected location'}...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ color: '#ef4444', marginBottom: '10px' }}>{error}</div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Location: {location || 'Not set'}</div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Geofips: {geofips || 'Not set'}</div>
      </div>
    );
  }
  
  // If no location is set or no data found, don't show anything
  if (!location || (!loading && dataRows.length === 0)) {
    return null;
  }

  const ratioChartContent = (
    <div style={{ height: 400, width: '100%', position: 'relative' }}>
      <canvas ref={chartRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );

  // Key metrics display
  const ratioKeyMetricsDisplay = keyMetrics && keyMetrics.housingUnits !== undefined && keyMetrics.employees !== undefined ? (
    <KeyMetrics>
      <KeyMetric
        value={keyMetrics.housingUnits.toLocaleString()}
        label={`Housing Units (${keyMetrics.latestYear})`}
        color="#3b82f6"
      />
      <KeyMetric
        value={keyMetrics.employees.toLocaleString()}
        label={`Employees (${keyMetrics.latestYear})`}
        color="#ef4444"
      />
      <KeyMetric
        value={keyMetrics.ratio}
        label="Housing Units per Employee"
        color="#10b981"
      />
    </KeyMetrics>
  ) : null;

  const projectionKeyMetricsDisplay = (
    <KeyMetrics>
      <KeyMetric 
        value="+12.4%" 
        label="Population growth since 2010"
        color="#1e3a8a"
      />
      <KeyMetric 
        value="2.1%" 
        label="Annual growth rate (2023)"
        color="#10b981"
      />
      <KeyMetric 
        value={
          projectionPopulationIn10Years != null
            ? projectionPopulationIn10Years.toLocaleString()
            : '—'
        }
        label={
          projectionTargetYear != null
            ? `Population projection in 10 years (${projectionTargetYear})`
            : 'Population projection in 10 years'
        }
        color="#f59e0b"
      />
    </KeyMetrics>
  );

  const projectionOverview = (
    <OverviewContent
      title="Population Projection Overview"
      description="This chart displays population growth trends and projections for the selected location, showing historical population counts from 2005 to present and projected future growth."
    >
      <p>{location} has experienced steady population growth over the past decade. Population trends directly impact infrastructure needs, housing demand, transportation, and climate planning.</p>
    </OverviewContent>
  );

  const projectionRelevance = (
    <RelevanceContent
      title="Relevance to Climate Action"
      description="Population growth directly impacts climate action planning by influencing infrastructure needs, resource consumption, transportation demand, and housing requirements."
    >
      <div className="methodology-details-simple">
        <h5>Infrastructure Planning</h5>
        <p>Growing populations require expanded infrastructure including transportation networks, energy systems, and water and wastewater facilities.</p>
      </div>
      <div className="methodology-details-simple">
        <h5>Resource Management</h5>
        <p>Population growth increases demand for energy, water, and transportation services, making efficiency and clean energy critical.</p>
      </div>
    </RelevanceContent>
  );

  const projectionEquity = (
    <EquityContent
      title="Equity Considerations"
      description="Population growth patterns can create or exacerbate inequities in access to services, housing, transportation, and climate resilience resources."
    >
      <div className="methodology-details-simple">
        <h5>Housing Affordability</h5>
        <p>Population growth often increases housing demand and costs, potentially displacing vulnerable populations.</p>
      </div>
      <div className="methodology-details-simple">
        <h5>Service Access</h5>
        <p>Rapid growth can strain services and infrastructure, leaving some communities underserved.</p>
      </div>
    </EquityContent>
  );

  const projectionMethodology = (
    <MethodologyContent
      title="Methodology"
      description="Population data comes from the US Census Bureau and American Community Survey, providing annual estimates for historical analysis and projections."
      dataSources={[
        'US Census Bureau population estimates (annual)',
        'American Community Survey 5-year estimates (demographics)',
        'Census Decennial Census data (baseline counts)'
      ]}
      methodology={[
        'Annual population estimates represent official counts between decennial censuses',
        'Population change calculated as percentage difference from baseline year (2010)',
        'Future projections based on historical trends'
      ]}
    />
  );

  const comparisonKeyMetricsDisplay = (
    <KeyMetrics>
      <KeyMetric
        value={
          comparisonCityPercentChange != null
            ? `${comparisonCityPercentChange >= 0 ? '+' : ''}${comparisonCityPercentChange.toFixed(1)}%`
            : '—'
        }
        label="City Population Percent Change (%)"
        color="#3b82f6"
      />
      <KeyMetric
        value={
          comparisonCaliforniaPercentChange != null
            ? `${comparisonCaliforniaPercentChange >= 0 ? '+' : ''}${comparisonCaliforniaPercentChange.toFixed(1)}%`
            : '—'
        }
        label="California Population Percent Change (%)"
        color="#10b981"
      />
    </KeyMetrics>
  );

  const comparisonOverview = (
    <OverviewContent
      title="Population Growth Overview"
      description="This chart shows population growth trends for the selected location and California using the first shared year as baseline."
    >
      <p>The comparison uses percent change from the first shared year so both series start at 0%.</p>
    </OverviewContent>
  );

  const comparisonMethodology = (
    <MethodologyContent
      title="Methodology"
      description="Population data comes from the US Census Bureau and American Community Survey."
      dataSources={[
        'US Census Bureau population estimates (annual)',
        'American Community Survey 5-year estimates (demographics)'
      ]}
      methodology={[
        'Population indexed to the first shared year between location and California',
        'Percent change = (population / baseline population - 1) × 100',
        'Chart includes years where both location and California data are available'
      ]}
    />
  );

  const comparisonRelevance = (
    <RelevanceContent
      title="Relevance"
      description="Comparing a city's growth from its 1990 baseline highlights how development has evolved over time."
    >
      <div className="methodology-details-simple">
        <h5>Growth Trends</h5>
        <p>Observing whether the city is growing faster or slower than its past rates helps planners identify patterns in population, housing, or economic development.</p>
      </div>
      <div className="methodology-details-simple">
        <h5>Planning &amp; Infrastructure</h5>
        <p>Understanding growth patterns informs decisions on transportation, utilities, public services, and community facilities, ensuring infrastructure keeps pace with changing needs.</p>
      </div>
      <div className="methodology-details-simple">
        <h5>Policy &amp; Benchmarking</h5>
        <p>Tracking growth supports evaluation of past policies and can guide future strategies in land use, housing, and economic planning.</p>
      </div>
    </RelevanceContent>
  );

  const comparisonEquity = (
    <EquityContent
      title="Equity"
      description="Growth patterns affect affordability, access, and the distribution of opportunities across the city."
    >
      <div className="methodology-details-simple">
        <h5>Housing &amp; Affordability</h5>
        <p>Rapid growth can increase competition for housing, driving up rents and home prices, particularly affecting low- and moderate-income residents.</p>
      </div>
      <div className="methodology-details-simple">
        <h5>Displacement &amp; Access</h5>
        <p>Uneven growth can exacerbate neighborhood disparities, influencing who has access to jobs, services, and community resources.</p>
      </div>
      <div className="methodology-details-simple">
        <h5>Service Equity</h5>
        <p>Areas experiencing faster growth may face pressure on public infrastructure, making equitable planning critical to ensure all residents benefit from development.</p>
      </div>
    </EquityContent>
  );

  const isRatioView = activeChart === 'ratio';
  const isProjectionView = activeChart === 'projection';
  const isComparisonView = activeChart === 'comparison';

  const chartContent = isProjectionView
    ? <PopulationGrowth location={location} />
    : isComparisonView
      ? <PopulationGrowthComparison location={location} />
      : ratioChartContent;

  const keyMetricsDisplay = isRatioView ? ratioKeyMetricsDisplay : isProjectionView ? projectionKeyMetricsDisplay : comparisonKeyMetricsDisplay;

  const overviewContent = isProjectionView
    ? projectionOverview
    : isComparisonView
      ? comparisonOverview
      : (
        <OverviewContent
          title="Overview"
          description={
            ratioNarrativeMeta
              ? `This chart compares the number of housing units to the number of employees in ${location} over time for the period of ${ratioNarrativeMeta.earliestYear} to ${ratioNarrativeMeta.latestYear}, helping assess the jobs-housing balance in the area.`
              : `This chart compares the number of housing units to the number of employees in ${location} over time, helping assess the jobs-housing balance in the area.`
          }
        >
          <div>
            {ratioNarrativeMeta ? (
              <p>Over the full period, the ratio of housing units to employees shows a {ratioNarrativeMeta.trend} trend.</p>
            ) : (
              <p>This chart shows the relationship between housing units and employment in the area over time.</p>
            )}
          </div>
        </OverviewContent>
      );

  const relevanceContent = isProjectionView
    ? projectionRelevance
    : isComparisonView
      ? comparisonRelevance
      : (
      <RelevanceContent
        title="Relevance"
        description="The relationship between housing units and employment is a key indicator in land use and transportation planning. If employment grows faster than housing, workers need to commute from other jurisdictions. If housing grows proportionally with employment, communities may better support local workforce housing and reduce commuting distances."
      >
        <div className="methodology-details-simple">
          <h5>Jobs-Housing Balance</h5>
          <p>The relationship between jobs and housing units helps assess whether residential development is keeping pace with economic growth. If the ratio of jobs to housing units increases, employment is growing faster than housing supply; if the ratio decreases, housing growth is outpacing job growth; and if stable, growth is occurring at similar rates.</p>
        </div>
        <div className="methodology-details-simple">
          <h5>Transportation and Commuting</h5>
          <p>When employment growth exceeds housing production, workers commute from outside the jurisdiction, which potentially increases vehicle miles traveled (VMT) and congestion. When housing growth keeps pace with employment, local workforce housing opportunities improve.</p>
        </div>
        <div className="methodology-details-simple">
          <h5>Infrastructure &amp; Service Demand</h5>
          <p>Changes in the relative growth of jobs and housing can affect demand for transportation networks, utilities, public services, and community facilities. Balanced growth can support more efficient infrastructure planning.</p>
        </div>
      </RelevanceContent>
    );

  const equityContent = isProjectionView
    ? projectionEquity
    : isComparisonView
      ? comparisonEquity
      : (
      <EquityContent
        title="Equity"
        description="The relationship between employment and housing growth has implications for affordability, workforce access, and risk of displacement."
      >
        <div className="methodology-details-simple">
          <h5>Workforce Housing Access</h5>
          <p>A widening imbalance between jobs and housing intensifies competition for available units, contributing to rising rents and home prices depending on market conditions. This means local workers (particularly lower and middle income households) may face greater difficulty securing housing near jobs.</p>
        </div>
        <div className="methodology-details-simple">
          <h5>Regional Disparities</h5>
          <p>When housing supply does not align with employment growth, commuting burdens may shift to neighboring communities, potentially worsening regional inequalities in transportation access and housing opportunity.</p>
        </div>
      </EquityContent>
    );

  const methodologyContent = isProjectionView
    ? projectionMethodology
    : isComparisonView
      ? comparisonMethodology
      : (
        <MethodologyContent
          title="Data Sources and Methodology"
          description="Housing units data is from US Census American Community Survey 5-Year Estimates. The number of employees data is from the ZIPCode Business Pattern (2012-2017) and County Business Pattern (2018-2023)."
          dataSources={[
            'US Census American Community Survey (ACS) 5-Year Estimates - Housing Units',
            'ZIPCode Business Pattern (2012-2017) - Employee counts',
            'County Business Pattern (2018-2023) - Employee counts',
            'Census relationship file for mapping ZIP Code to City'
          ]}
          methodology={[
            'Number of employees is mapped from ZIP Code to City based on relationship file from Census',
            'Housing units data comes from ACS 5-Year Estimates',
            'Ratio calculated as housing units divided by employees',
            'Time series analysis from 2012-2023 where data available',
            'Data filtered by geography type (place, county, or state) and location'
          ]}
        />
      );

  return (
    <ChartLayout
      title="Housing, Population & Employment Trends"
      key={`housing-employees-${location}`}
      description={`${location} • Source: US Census ACS 5-Year Estimates & ZIPCode/County Business Patterns`}
      chartType="line"
      keyMetrics={keyMetricsDisplay}
      chartContent={chartContent}
      activeView={activeView}
      onViewChange={setActiveView}
      chartSelector={{
        value: activeChart,
        onChange: (value: string) => setActiveChart(value as 'ratio' | 'projection' | 'comparison'),
        options: [
          { value: 'ratio', label: 'Housing to Employees Ratio' },
          { value: 'projection', label: 'Population Projection' },
          { value: 'comparison', label: 'Population Growth' }
        ]
      }}
      showRelevanceEquity={true}
      layout="side-by-side"
      onDownloadPNG={
        isProjectionView
          ? () => {
              const canvas = document.querySelector('#population-growth-chart canvas') as HTMLCanvasElement;
              if (canvas) {
                const link = document.createElement('a');
                link.download = `${location.replace(/\s+/g, '_')}_Population_Projection.png`;
                link.href = canvas.toDataURL();
                link.click();
              }
            }
          : isComparisonView
            ? () => {
                const canvas = document.querySelector('#population-growth-comparison-chart canvas') as HTMLCanvasElement;
                if (canvas) {
                  const link = document.createElement('a');
                  link.download = `${location.replace(/\s+/g, '_')}_Population_Growth.png`;
                  link.href = canvas.toDataURL();
                  link.click();
                }
              }
            : handleDownloadPNG
      }
      onDownloadCSV={isProjectionView || isComparisonView ? undefined : handleDownloadCSV}
      pngFilename={
        isProjectionView
          ? formatFilename(location, 'Population_Projection')
          : isComparisonView
            ? formatFilename(location, 'Population_Growth')
            : formatFilename(location, 'Housing_to_Employees')
      }
      csvFilename={
        isProjectionView || isComparisonView
          ? formatFilename(location, 'Population_Data')
          : formatFilename(location, 'Housing_to_Employees_Data')
      }
      enableComments={true}
      location={location}
      overviewContent={overviewContent}
      relevanceContent={relevanceContent}
      equityContent={equityContent}
      methodologyContent={methodologyContent}
    />
  );
};

export default HousingToEmployees;

